import { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, LogIn } from 'lucide-react';
import { cosmetic, DEFAULT_LOOK } from '../../shared/cosmetics.ts';
import { BOX_ODDS } from '../../shared/economy.ts';
import type { GiftBox } from '../../shared/protocol.ts';
import { useAuth } from '../app/auth.ts';
import { useT } from '../app/preferences.ts';
import { Dialog } from '../components/Dialog.tsx';
import { Link } from '../components/Link.tsx';
import { CosmeticPreview, GoldAmount } from '../components/PlayerLook.tsx';
import { useCosmeticName } from '../app/cosmeticName.ts';
import { fetchBoxes, openBox } from '../online/progressionApi.ts';

/** The "shake" before the reveal; long enough to feel exciting, short enough not to annoy. */
const OPENING_MS = 1400;

export function GiftsPage() {
  const t = useT();
  const { user, canSignIn, signIn, refresh } = useAuth();
  const [boxes, setBoxes] = useState<GiftBox[] | null>(null);
  const [opening, setOpening] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<GiftBox | null>(null);
  const signedIn = Boolean(user?.username);

  const load = useCallback(() => {
    void fetchBoxes()
      .then((r) => setBoxes(r.boxes))
      .catch(() => setBoxes([]));
  }, []);

  useEffect(() => {
    if (signedIn) load();
  }, [signedIn, load]);

  const open = async (box: GiftBox) => {
    if (opening) return;
    setOpening(box.id);
    const [result] = await Promise.all([openBox(box.id), new Promise((r) => setTimeout(r, OPENING_MS))]);
    setOpening(null);
    if (result.ok) {
      setRevealed(result.data.box);
      setBoxes((prev) => prev?.map((b) => (b.id === box.id ? result.data.box : b)) ?? prev);
      void refresh();
    }
  };

  if (!signedIn) {
    return (
      <div className="center-state pop-in">
        <span className="result-icon emoji" aria-hidden="true">
          🎁
        </span>
        <p>{t('shop.signIn')}</p>
        {canSignIn && (
          <button type="button" className="btn btn-blue" onClick={() => signIn('/gifts')}>
            <LogIn size={22} /> {t('auth.signIn')}
          </button>
        )}
      </div>
    );
  }

  const closed = boxes?.filter((b) => !b.openedAt) ?? [];
  const opened = boxes?.filter((b) => b.openedAt) ?? [];

  return (
    <div className="page-fill contained">
      <div className="page-top">
        <Link href="/" className="back-link">
          <ArrowLeft size={22} strokeWidth={2.5} /> {t('nav.home')}
        </Link>
        <h1 className="page-title">🎁 {t('gifts.title')}</h1>
      </div>
      <p className="page-lead">{t('gifts.lead')}</p>

      {/* The header stays put; boxes, history and odds scroll in their own area. */}
      <div className="page-scroll">
        {boxes === null ? (
          <div className="center-state" role="status">
            <span className="spinner" />
          </div>
        ) : closed.length === 0 ? (
          <div className="center-state">
            <span className="result-icon emoji" aria-hidden="true">
              📦
            </span>
            <p>{t('gifts.empty')}</p>
            <Link href="/leaderboard" className="btn btn-primary">
              🏆 {t('nav.leaderboard')}
            </Link>
          </div>
        ) : (
          <ul className="gift-grid pop-in">
            {closed.map((box) => (
              <li key={box.id} className={`gift-card card tier-${box.tier}`}>
                <span className={`gift-art emoji${opening === box.id ? ' shaking' : ''}`} aria-hidden="true">
                  🎁
                </span>
                <strong>{t(`gifts.tier.${box.tier}`)}</strong>
                <span className="gift-from">
                  {t('gifts.from', {
                    place: t(`gifts.place${box.rank as 1 | 2 | 3}`),
                    period: t(box.period === 'day' ? 'gifts.periodDay' : 'gifts.periodWeek'),
                  })}
                </span>
                <button type="button" className="btn btn-primary" onClick={() => open(box)} disabled={opening !== null}>
                  {opening === box.id ? t('gifts.opening') : t('gifts.open')}
                </button>
              </li>
            ))}
          </ul>
        )}

        {opened.length > 0 && (
          <ul className="gift-history">
            {opened.map((box) => (
              <li key={box.id} className="badge">
                <span className="emoji" aria-hidden="true">
                  📭
                </span>
                {t(`gifts.tier.${box.tier}`)} · {box.reward && <GoldAmount amount={box.reward.gold + box.reward.duplicateGold} />}
                {box.reward?.item && <span className="emoji">{cosmetic(box.reward.item)?.emoji ?? '🖼️'}</span>}
              </li>
            ))}
          </ul>
        )}

        <details className="gift-odds card">
          <summary>{t('gifts.odds')}</summary>
          <table>
            <tbody>
              {(['day', 'week'] as const).flatMap((period) =>
                (['gold', 'silver', 'bronze'] as const).map((tier) => {
                  const odds = BOX_ODDS[period][tier];
                  return (
                    <tr key={`${period}-${tier}`}>
                      <th scope="row">
                        {t(period === 'day' ? 'gifts.periodDay' : 'gifts.periodWeek')} · {t(`gifts.tier.${tier}`)}
                      </th>
                      <td>
                        {t('gifts.oddsRow', {
                          min: odds.gold[0],
                          max: odds.gold[1],
                          chance: (odds.itemChance * 100).toLocaleString(undefined, { maximumFractionDigits: 1 }),
                        })}
                      </td>
                    </tr>
                  );
                }),
              )}
            </tbody>
          </table>
        </details>
      </div>

      <RevealDialog box={revealed} name={user!.username!} onClose={() => setRevealed(null)} />
    </div>
  );
}

function RevealDialog({ box, name, onClose }: { box: GiftBox | null; name: string; onClose: () => void }) {
  const t = useT();
  const { user } = useAuth();
  const reward = box?.reward;
  const itemName = useCosmeticName();
  return (
    <Dialog
      open={Boolean(reward)}
      onClose={onClose}
      title={t('gifts.youGot')}
      bare
      footer={
        <button type="button" className="btn btn-primary btn-lg" onClick={onClose} autoFocus>
          {t('common.close')}
        </button>
      }
    >
      {reward && (
        <div className="result-hero reveal">
          <span className="reveal-burst emoji" aria-hidden="true">
            🎉
          </span>
          <h2 className="result-title">{t('gifts.youGot')}</h2>
          <p className="reveal-gold">
            <GoldAmount amount={reward.gold + reward.duplicateGold} signed />
          </p>
          {reward.item && (
            <div className="reveal-item">
              <span className="badge badge-accent">{t('gifts.item')}</span>
              <CosmeticPreview id={reward.item} name={name} look={user?.look ?? DEFAULT_LOOK} size="xl" />
              <strong className="shop-name">{itemName(reward.item)}</strong>
            </div>
          )}
          {reward.duplicateGold > 0 && <p className="result-reason">{t('gifts.duplicate', { amount: reward.duplicateGold })}</p>}
        </div>
      )}
    </Dialog>
  );
}
